import { CancelablePromise } from "../cancelablePromise";
import StorageBase from "../storage/storage.base";
import storage, { getCurrentUser, getFullKey, handleParsingError, StorageKeys } from "../storage/storage";
import * as logger from "../logger";
import { defaultUsers } from "../../store/users/reducers";
import { Source } from "../logger";
import { versionComparator } from "../helpers";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";

let isSecureStorageComparedCached: Promise<void> | undefined = undefined;

export function isNativeStorageAvailable(): boolean {
    return (
        !!window.geotabModules &&
        !!window.geotabModules.secureStorage &&
        !!window.device &&
        !(window.device?.platform === "Android" && versionComparator(window.device?.version, "6.7.0") < 0) &&
        !(window.device?.platform === "iOS" && versionComparator(window.device?.version, "6.7.1") < 0)
    );
}

function clearCaches() {
    // Clear the cache for secure storage compare
    isSecureStorageComparedCached = undefined;
}

/**
 * Logs a performance metric message along with the time taken since the provided start time.
 *
 * @param message - A descriptive message for the performance metric.
 * @param startTimeMs - The start time in milliseconds, typically from `performance.now()`.
 */
function logPerformanceMetric(message: string, startTimeMs: number): void {
    let timeTaken = performance.now() - startTimeMs;
    const userObject = storage.get<IIdDictionary<IUser>>(StorageKeys.USERS_KEY, true) || defaultUsers;

    const logMessage = `${ message } ${ timeTaken }`;

    logger.log(Source.STORAGE, logMessage, userObject);
}

/**
 * Retrieves all keys stored in secure storage.
 *
 * This function attempts to fetch all keys from the secure storage if native storage is available.
 * It measures and logs the performance time taken to fetch the keys.
 *
 * @returns {CancelablePromise<string[] | null>} A promise that resolves to:
 *   - An array of strings representing all keys in secure storage
 *   - Null if native storage is not available or there was an error fetching the keys
 */
function keys(): CancelablePromise<string[] | null> {
    return new CancelablePromise<string[] | null>(resolve => {
        try {
            if (isNativeStorageAvailable()) {
                if (!window.geotabModules?.secureStorage?.keys) {
                    throw new Error("Secure storage keys method is not available");
                }
                const startTimeMs = performance.now();
                window.geotabModules?.secureStorage.keys(undefined, (err?: Error, response?: string) => {
                    if (err || !Array.isArray(response)) {
                        logger.error(logger.Source.STORAGE, `secureStorage: Failed to fetch all keys ${ err?.message }`);
                        resolve(null);
                    } else {
                        resolve(response);
                    }
                    logPerformanceMetric("Time taken to fetch all the keys from secure storage", startTimeMs);
                });
            } else {
                let platformInfo = window.device?.platform ? `${ window.device.platform } - ${ window.device.version }` : "Unknown platform";
                logger.log(
                    logger.Source.STORAGE,
                    `secureStorage: Native storage is not available for ${ platformInfo }`
                );
                resolve(null);
            }
        } catch (error) {
            logger.error(logger.Source.STORAGE, `secureStorage: Unexpected error in keys: ${ (error as Error).message }`);
            resolve(null);
        }
    });
}

/**
 * Compares values stored in secure storage with their corresponding values in local storage.
 * This function ensures data consistency between the two storage mechanisms.
 *
 * The function maintains a promise to prevent multiple concurrent comparisons and is executed
 * only once per app launch. Subsequent calls will return the result of the initial comparison.
 *
 * @returns {Promise<void>} A promise that resolves when the comparison is complete
 * @remarks
 * - If the secure storage is empty, the function will log this and return immediately
 * - For each key found in secure storage, the function retrieves and compares the value
 *   with its local storage counterpart
 * - Any mismatches are logged as errors
 * - Performance timing is captured and logged upon completion
 */
export async function compareStorage() {
    if (isSecureStorageComparedCached) {
        return isSecureStorageComparedCached;
    }

    isSecureStorageComparedCached = keys().then(secureStorageKeys => {
        const startTimeMs = performance.now();
        if (!secureStorageKeys || secureStorageKeys.length === 0) {
            // Nothing to compare, just return
            return;
        }

        const userObject = storage.get<IIdDictionary<IUser>>(StorageKeys.USERS_KEY, true) || defaultUsers;
        Promise.all(
            secureStorageKeys.map(async key => {
                let localStorageVal = StorageBase.get(key);
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                const secureStorageVal = await getItem(key, false, undefined, true);
                if (secureStorageVal !== localStorageVal) {
                    logger.error(Source.STORAGE, `secureStorage: Key ${ key } does not match local storage value`, userObject);
                }
            })
        ).then(() => {
            logPerformanceMetric(`Time taken for comparing secure storage`, startTimeMs);
        });
    });
    return isSecureStorageComparedCached;
}

function setItemNative(key: string, overrideUser: string | undefined, forceKey: undefined | boolean, data): CancelablePromise<string | null> {
    return new CancelablePromise<string | null>(resolve => {
        const startTimeMs = performance.now();
        let storageKeyToSet = getFullKey(key, overrideUser, forceKey);
        window.geotabModules?.secureStorage.setItem(
            {
                key: storageKeyToSet,
                value: typeof data === "object" ? JSON.stringify(data) : data
            },
            (err?: Error, response?: string) => {
                if (response) {
                    logger.log(logger.Source.STORAGE, `secureStorage: Successfully saved key ${ response } `);
                    resolve(response);
                }
                if (err) {
                    logger.error(logger.Source.STORAGE, `secureStorage: Failed to set key ${ storageKeyToSet },  ${ err?.message }`);
                    resolve(null);
                }
                logPerformanceMetric(`Time taken for saving into secure storage ${ key }`, startTimeMs);
            }
        );
    });
}

export function removeItem(key: string, overrideUser?: string, forceKey?: boolean): CancelablePromise<string | null> {
    return new CancelablePromise<string | null>(resolve => {
        try {
            if (!window.geotabModules?.secureStorage?.removeItem || !isNativeStorageAvailable()) {
                throw new Error("Secure storage removeItem method is not available");
            }

            window.geotabModules?.secureStorage.removeItem(getFullKey(key, overrideUser, forceKey), (err?: Error, response?: string) => {
                if (response) {
                    logger.log(logger.Source.STORAGE, `secureStorage: Successfully removed key ${ response } `);
                    resolve(response);
                } else {
                    logger.error(logger.Source.STORAGE, `secureStorage: Failed to remove key ${ key },  ${ err?.message }`);
                    resolve(null);
                }
            });
        } catch (error) {
            logger.error(logger.Source.STORAGE, `secureStorage: Unexpected error in removeItem: ${ error.message }`);
            resolve(null);
        }
    });
}

/**
 * Retrieves an item from secure storage by key.
 *
 * @template T - The expected return type. Defaults to `string`.
 * @param key - The key to fetch from secure storage.
 * @param jsonParse - If `true`, attempts to parse the stored value as JSON.
 * @param overrideUser - Optional user identifier to override the default user context.
 * @param forceKey - If `true`, forces the use of the provided key without modification.
 * @returns A `CancelablePromise` that resolves to the retrieved item of type `T`, or `null` if not found or on error.
 *
 * @throws {Error} If the secure storage API is not available.
 *
 * @remarks
 * - If `jsonParse` is enabled and parsing fails, the item is removed from storage and `null` is returned.
 * - Logs performance metrics and errors for debugging and monitoring.
 */
export function getItem<T = string>(key: string, jsonParse = false, overrideUser?: string, forceKey?: boolean): CancelablePromise<T | null> {
    return new CancelablePromise<T | null>(resolve => {
        let nativeStorageAvailable = isNativeStorageAvailable();
        if (!nativeStorageAvailable) {
            resolve(null);
        } else {
            try {
                if (!window.geotabModules?.secureStorage?.getItem) {
                    throw new Error("Secure storage getItem method is not available");
                }
                const startTimeMs = performance.now();
                let storageKeyToGet = getFullKey(key, overrideUser, forceKey);
                window.geotabModules?.secureStorage.getItem(storageKeyToGet, (err?: Error, response?: string) => {
                    if (response) {
                        logger.log(logger.Source.STORAGE, `secureStorage: Successfully fetched for key ${ storageKeyToGet } `);
                        let result: T | null;
                        if (response && jsonParse) {
                            try {
                                result = JSON.parse(response) as T;
                            } catch (e) {
                                removeItem(key, overrideUser, forceKey);
                                const currentUser = getCurrentUser();
                                handleParsingError(
                                    `Unable to parse string '${ key }' from secureStorage: ${ response }`,
                                    key,
                                    currentUser !== null ? currentUser : undefined
                                );
                                result = null;
                            }
                        } else {
                            result = response as T;
                        }
                        resolve(result);
                    } else {
                        logger.error(logger.Source.STORAGE, `secureStorage: Failed to fetch key ${ storageKeyToGet },  ${ err?.message }`);
                        resolve(null);
                    }
                    logPerformanceMetric("Time taken for fetching from secure storage", startTimeMs);
                });
            } catch (err) {
                logger.error(logger.Source.STORAGE, `secureStorage: Unexpected error in getItem: ${ (err as Error).message }`);
                resolve(null);
            }
        }
    });
}

export function setItem(key: string, data, overrideUser?: string, forceKey?: boolean): CancelablePromise<string | null> {
    return new CancelablePromise<string | null>(resolve => {
        try {
            if (isNativeStorageAvailable()) {
                if (!window.geotabModules?.secureStorage?.setItem) {
                    throw new Error("Secure storage setItem method is not available");
                }

                // compareStorage between secure and localstorage on first call
                setItemNative(key, overrideUser, forceKey, data).then(result => {
                    void compareStorage();
                    resolve(result);
                });
            } else {
                let platformInfo = window.device?.platform ? `${ window.device.platform } - ${ window.device.version }` : "Unknown platform";
                logger.log(
                    logger.Source.STORAGE,
                    `secureStorage: Native storage is not available for ${ platformInfo }`
                );
                resolve(null);
            }
        } catch (error) {
            logger.error(logger.Source.STORAGE, `secureStorage: Unexpected error in setItem: ${ error.message }`);
            resolve(null);
        }
    });
}

export async function clear() {
    const secureStorageKeys = await keys();
    let ignoredPattern = new RegExp(`${ DRIVE_STORAGE_PREFIX }@`); //geotabDrive_@
    if (!secureStorageKeys || secureStorageKeys.length === 0) {
        clearCaches();
        // Nothing to clear, just return
        return;
    }
    for (const key of secureStorageKeys) {
        if (key.match(ignoredPattern)) {
            continue;
        }
        if (key.indexOf(DRIVE_STORAGE_PREFIX) === -1) {
            await removeItem(key, undefined, true);
        }
    }
    clearCaches();
}
